import {
    AnalyticsUserEvent,
    type TransactionsWidgetProps,
    type TransactionWidgetItem,
} from '@fruitful/types';
import React, { useMemo, useState } from 'react';
import { H5Emphasized, YStack } from 'smoothie';

import { type Transaction } from '../../../banking/types/Transactions';
import { track } from '../../../logger/Logger';
import { useEnrichedTransaction } from '../../hooks/useEnrichedTransaction';
import { SproutTransactionDetailsSheet } from './SproutTransactionDetailsSheet';
import { TransactionGroupBox } from './TransactionGroupBox';

const toTransaction = (item: TransactionWidgetItem): Transaction => ({
    uuid: item.id,
    type: item.type ?? 'ach',
    subtype: item.subtype ?? '',
    status: item.status ?? 'posted',
    transactionTime: item.transactionTime,
    dcSign: item.dcSign,
    amount: item.amountCents,
    isPayment: item.isPayment ?? false,
    description: item.description,
    formattedType: '',
});

/** Parse YYYY-MM-DD as a local calendar date; returns null if invalid. */
const parseLocalYmd = (dateStr: string): Date | null => {
    const parts = dateStr.split('-').map(Number);
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];
    if (
        year == null ||
        month == null ||
        day == null ||
        Number.isNaN(year) ||
        Number.isNaN(month) ||
        Number.isNaN(day)
    ) {
        return null;
    }
    return new Date(year, month - 1, day);
};

const formatDayHeader = (date: Date, includeYear: boolean): string => {
    return new Intl.DateTimeFormat(
        'en-US',
        includeYear
            ? {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              }
            : {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
              },
    ).format(date);
};

export const TransactionsWidget = ({
    title,
    groupBy,
    groups,
}: TransactionsWidgetProps) => {
    const [selected, setSelected] = useState<{
        uuid: string;
        syncteraAccountId: string;
    } | null>(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    // The Transaction type doesn't carry the account id, so keep a lookup from
    // the raw agent data to resolve it when a row is tapped.
    const syncteraAccountIdByUuid = useMemo(() => {
        const map = new Map<string, string>();
        for (const group of groups) {
            for (const transaction of group.transactions) {
                if (transaction.syncteraAccountId != null) {
                    map.set(transaction.id, transaction.syncteraAccountId);
                }
            }
        }
        return map;
    }, [groups]);

    const includeYearInDayHeaders = useMemo(() => {
        if (groupBy !== 'day') {
            return false;
        }
        const currentYear = new Date().getFullYear();
        return groups.some((group) => {
            const parsed = parseLocalYmd(group.key);
            return parsed != null && parsed.getFullYear() !== currentYear;
        });
    }, [groupBy, groups]);

    const handleOpenTransaction = (transaction: Transaction) => {
        const syncteraAccountId = syncteraAccountIdByUuid.get(transaction.uuid);
        if (syncteraAccountId == null) {
            return;
        }
        track(AnalyticsUserEvent.AI_CHAT_WIDGET_TRANSACTIONS_OPEN, {
            transactionType: transaction.type,
            transactionStatus: transaction.status,
        });
        setSelected({
            uuid: transaction.uuid,
            syncteraAccountId,
        });
        setIsSheetOpen(true);
    };

    // Fetch here (in the provider tree) rather than inside the sheet, whose
    // portal mounts outside AuthedUserProvider.
    const { account, transaction, isLoading, notFound } =
        useEnrichedTransaction({
            uuid: selected?.uuid ?? '',
            syncteraAccountId: selected?.syncteraAccountId ?? '',
            enabled: selected != null,
        });

    const showDateSubtitle = groupBy !== 'day';

    return (
        <YStack width="100%" gap={16}>
            {title != null && title !== '' && (
                <H5Emphasized color="$black">{title}</H5Emphasized>
            )}
            {groups.map((group, index) => {
                let header = group.key;
                if (groupBy === 'day') {
                    const parsed = parseLocalYmd(group.key);
                    if (parsed != null) {
                        header = formatDayHeader(
                            parsed,
                            includeYearInDayHeaders,
                        );
                    }
                }

                return (
                    <TransactionGroupBox
                        key={`${group.key}-${index.toString()}`}
                        header={header}
                        transactions={group.transactions.map(toTransaction)}
                        showDateSubtitle={showDateSubtitle}
                        onOpenTransaction={
                            syncteraAccountIdByUuid.size > 0
                                ? handleOpenTransaction
                                : undefined
                        }
                    />
                );
            })}
            <SproutTransactionDetailsSheet
                isOpen={isSheetOpen}
                onClose={() => {
                    // Keep `selected` (and thus the fetched details) mounted while
                    // the sheet slides closed so it doesn't briefly flash the
                    // "not available" fallback. It's overwritten on the next open.
                    setIsSheetOpen(false);
                }}
                syncteraAccountId={selected?.syncteraAccountId ?? ''}
                account={account}
                transaction={transaction}
                isLoading={isLoading}
                notFound={notFound}
            />
        </YStack>
    );
};
